import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UserNotificationAction } from '@arc/arc-shared-frontend';
import {
  DisputeCategory,
  DisputeReason,
  DisputeService,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  DisputeActionsUnion,
  GetDisputeCategoriesCompleteAction,
  GetDisputeCategoriesErrorAction,
  GetDisputeReasonsCompleteAction,
  GetDisputeReasonsErrorAction,
} from '../actions/dispute.actions';
import { DisputeActions } from '../actions';
import { Store } from '@ngrx/store';
import { selectVirtualOtherwisePrimaryTenantId } from '../../session-store';
import { concatLatestFrom } from '@ngrx/operators';
import {
  PoiPadResultComponentActions,
  PoiPadResultComponentPaymentRulesActions,
} from '../../component-actions';
import { selectAllowDisputeReasons } from '../../payment-rules';

@Injectable()
export class DisputeEffects {
  constructor(
    private actions$: Actions<DisputeActionsUnion>,
    private disputeService: DisputeService,
    private store: Store,
  ) {}

  getDisputeCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DisputeActions.GetDisputeCategoriesAction,
        PoiPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(selectAllowDisputeReasons),
      ]),
      filter(([, , allowDisputeReasons]) => allowDisputeReasons),
      exhaustMap(([, tenantId]) => {
        return this.disputeService.getDisputeCategoriesV1(tenantId).pipe(
          map((categories: DisputeCategory[]) => {
            return GetDisputeCategoriesCompleteAction({
              payload: categories,
            });
          }),
          catchError(() =>
            of(
              GetDisputeCategoriesErrorAction({
                payload: 'No Dispute Categories Found',
              }),
            ),
          ),
        );
      }),
    ),
  );

  getDisputeCategoriesError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisputeActions.GetDisputeCategoriesErrorAction.type),
      map((action) => action.payload),
      exhaustMap((payload) =>
        of(
          new UserNotificationAction({
            type: 'error',
            message: payload,
          }),
        ),
      ),
    ),
  );

  getDisputeReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DisputeActions.GetDisputeReasonsAction,
        PoiPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
        this.store.select(selectAllowDisputeReasons),
      ]),
      filter(([, , allowDisputeReasons]) => allowDisputeReasons),
      exhaustMap(([, tenantId]) => {
        return this.disputeService.getDisputeReasonsV1(tenantId).pipe(
          map((reasons: DisputeReason[]) => {
            return GetDisputeReasonsCompleteAction({
              payload: reasons,
            });
          }),
          catchError(() =>
            of(
              GetDisputeReasonsErrorAction({
                payload: 'No Dispute Reasons Found',
              }),
            ),
          ),
        );
      }),
    ),
  );

  getDisputeReasonsError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisputeActions.GetDisputeReasonsErrorAction.type),
      map((action) => action.payload),
      exhaustMap((payload) =>
        of(
          new UserNotificationAction({
            type: 'error',
            message: payload,
          }),
        ),
      ),
    ),
  );
}
